import { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import MDBox from 'components/MDBox';
import ReportsLineChart from 'examples/Charts/LineCharts/ReportsLineChart';
import ComplexStatisticsCard from 'examples/Cards/StatisticsCards/ComplexStatisticsCard';
import RecentProperties from './RecentProperties';
import {
  getPropertyChart,
  getInvestorChart,
  getWidgets,
} from 'services/dashboard.service';
import { formatCurrency, getUSNumberFormat } from 'helpers/formatData';
import { CircularProgress } from '@mui/material';

function Dashboard() {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [widgets, setWidgets] = useState({
    propertyData: {
      totalPropertyCount: 0,
      commingSoonPropertyCount: 0,
    },
    investorData: {
      totalInvestorCount: 0,
      currentMonthInvestorCount: 0,
    },
    revenueData: {
      totalRevenue: 0,
      percentageDeference: 0,
    },
    recurringRevenueData: {
      totalRecurringRevenue: 0,
      percentageDeference: 0,
    },
    primaryPropertyCalculation: {
      propertyCount: 0,
      totalTransactionsCount: 0,
      totalInvestorsCount: 0,
      totalVolume: 0,
      totalInvestmentedAmount: 0,
    },
    secondaryPropertyCalculation: {
      propertyCount: 0,
      totalTransactionsCount: 0,
      totalInvestorsCount: 0,
      totalVolume: 0,
    },
    dividendData: {
      lastQuarterAmount: 0,
      lastYearAmount: 0,
      totalAmount: 0,
    },
  });
  const [propertyChart, setPropertyChart] = useState({
    labels: [],
    datasets: { label: 'Property', data: [] },
    percentageDeference: 0,
  });
  const [investorChart, setInvestorChart] = useState({
    labels: [],
    datasets: { label: 'Investors', data: [] },
    percentageDeference: 0,
  });
  //handler function
  const setNumberNotation = (val) => {
    return val >= 0 ? '+' + val : val;
  };
  const handleNavigation = (val) => {
    navigate(val);
  };
  // API Integration
  const fetchWidgetsData = async () => {
    setLoader(true);
    try {
      const res = await getWidgets();
      if (!res.isError) {
        setWidgets(res.data);
      } else {
        toast.error(res?.message);
      }
    } catch (error) {
      const { response } = error;
      toast.error(response?.data?.message);
    }
    setLoader(false);
  };
  const fetchPropertyChartData = async () => {
    setLoader(true);
    try {
      const res = await getPropertyChart();
      if (!res.isError) {
        setPropertyChart((state) => {
          return {
            ...state,
            labels: res.data?.labels,
            datasets: { ...state.datasets, data: res.data?.data },
            percentageDeference: res.data?.percentageDeference,
          };
        });
      } else {
        toast.error(res?.message);
      }
    } catch (error) {
      const { response } = error;
      toast.error(response?.data?.message);
    }
    setLoader(false);
  };
  const fetchInvestorChartData = async () => {
    setLoader(true);
    try {
      const res = await getInvestorChart();
      if (!res.isError) {
        setInvestorChart((state) => {
          return {
            ...state,
            labels: res.data?.labels,
            datasets: { ...state.datasets, data: res.data?.data },
            percentageDeference: res.data?.percentageDeference,
          };
        });
      } else {
        toast.error(res?.message);
      }
    } catch (error) {
      const { response } = error;
      toast.error(response?.data?.message);
    }
    setLoader(false);
  };
  useEffect(() => {
    fetchWidgetsData();
    fetchPropertyChartData();
    fetchInvestorChartData();
  }, []);
  return (
    <>
      {loader ? (
        <MDBox my={3} textAlign="center">
          <CircularProgress color="inherit" size={30} />
        </MDBox>
      ) : (
        <MDBox py={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5} onClick={() => handleNavigation('/properties')}>
                <ComplexStatisticsCard
                  color="info"
                  icon="home"
                  title="Total Properties"
                  count={getUSNumberFormat(
                    widgets.propertyData.totalPropertyCount
                  )}
                  percentage={{
                    color: 'warning',
                    amount: getUSNumberFormat(
                      widgets.propertyData.commingSoonPropertyCount
                    ),
                    label: 'coming soon',
                  }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5} onClick={() => handleNavigation('/users')}>
                <ComplexStatisticsCard
                  color="info"
                  icon="people_alt"
                  title="Total Investors"
                  count={getUSNumberFormat(
                    widgets.investorData.totalInvestorCount
                  )}
                  percentage={{
                    color: `${
                      widgets.investorData.currentMonthInvestorCount >= 0
                        ? 'success'
                        : 'error'
                    }`,
                    amount: `${setNumberNotation(
                      widgets.investorData.currentMonthInvestorCount
                    )}`,
                    label: 'since last month',
                  }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5} onClick={() => handleNavigation('/revenue')}>
                <ComplexStatisticsCard
                  color="success"
                  icon="attach_money"
                  title="Revenue"
                  count={formatCurrency(widgets.revenueData.totalRevenue)}
                  percentage={{
                    color: `${
                      (Number(widgets.revenueData.percentageDeference) || 0) >=
                      0
                        ? 'success'
                        : 'error'
                    }`,
                    amount:
                      setNumberNotation(
                        Number(widgets.revenueData.percentageDeference) || 0
                      ) + '%',
                    label: 'than last month',
                  }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox
                mb={1.5}
                onClick={() => handleNavigation('/recurring-revenue')}
              >
                <ComplexStatisticsCard
                  color="success"
                  icon="currency_exchange"
                  title="Recurring Revenue"
                  count={formatCurrency(
                    widgets.recurringRevenueData.totalRecurringRevenue
                  )}
                  percentage={{
                    color: `${
                      (Number(
                        widgets.recurringRevenueData.percentageDeference
                      ) || 0) >= 0
                        ? 'success'
                        : 'error'
                    }`,
                    amount:
                      setNumberNotation(
                        Number(
                          widgets.recurringRevenueData.percentageDeference
                        ) || 0
                      ) + '%',
                    label: 'than last month',
                  }}
                />
              </MDBox>
            </Grid>
          </Grid>
          <MDBox mt={2} mb={4.5}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={4}>
                <MDBox mb={1.5} onClick={() => handleNavigation('/properties')}>
                  <ComplexStatisticsCard
                    color="info"
                    icon="home"
                    title="Primary Properties"
                    count={getUSNumberFormat(
                      widgets.primaryPropertyCalculation.propertyCount
                    )}
                    percentage={false}
                    details={[
                      {
                        label: 'Total Transactions',
                        amount: getUSNumberFormat(
                          widgets.primaryPropertyCalculation
                            .totalTransactionsCount
                        ),
                      },
                      {
                        label: 'Total Investors',
                        amount: getUSNumberFormat(
                          widgets.primaryPropertyCalculation.totalInvestorsCount
                        ),
                      },
                      {
                        label: 'Total Volume',
                        amount: getUSNumberFormat(
                          widgets.primaryPropertyCalculation.totalVolume
                        ),
                      },
                      {
                        label: 'Total Investments',
                        amount: formatCurrency(
                          widgets.primaryPropertyCalculation?.totalInvestmentedAmount ?? 0
                        ),
                      },
                    ]}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <MDBox mb={1.5} onClick={() => handleNavigation('/properties')}>
                  <ComplexStatisticsCard
                    color="info"
                    icon="home"
                    title="Secondary Properties"
                    count={getUSNumberFormat(
                      widgets.secondaryPropertyCalculation.propertyCount
                    )}
                    percentage={false}
                    details={[
                      {
                        label: 'Total Transactions',
                        amount: getUSNumberFormat(
                          widgets.secondaryPropertyCalculation
                            .totalTransactionsCount
                        ),
                      },
                      {
                        label: 'Total Investors',
                        amount: getUSNumberFormat(
                          widgets.secondaryPropertyCalculation
                            .totalInvestorsCount
                        ),
                      },
                      {
                        label: 'Total Volume',
                        amount: getUSNumberFormat(
                          widgets.secondaryPropertyCalculation.totalVolume
                        ),
                      },
                    ]}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <MDBox
                  mb={1.5}
                  onClick={() => handleNavigation('/finance#dividend')}
                >
                  <ComplexStatisticsCard
                    color="success"
                    icon="reply"
                    title="Total Dividend Shared"
                    count={formatCurrency(widgets.dividendData.totalAmount)}
                    percentage={false}
                    details={[
                      {
                        label: 'Last Quarter',
                        amount: formatCurrency(
                          widgets.dividendData.lastQuarterAmount
                        ),
                      },
                      {
                        label: 'Last Year',
                        amount: formatCurrency(
                          widgets.dividendData.lastYearAmount
                        ),
                      },
                    ]}
                  />
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
          <MDBox mb={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <MDBox mb={3}>
                  <ReportsLineChart
                    color="info"
                    title="Total Properties"
                    description={
                      <>
                        (
                        <strong>
                          {setNumberNotation(propertyChart.percentageDeference)}
                          %
                        </strong>
                        ){' '}
                        {propertyChart.percentageDeference < 0
                          ? 'decrease'
                          : 'increase'}{' '}
                        in last 30 days
                      </>
                    }
                    chart={propertyChart}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={3}>
                  <ReportsLineChart
                    color="success"
                    title="Total Investors"
                    description={
                      <>
                        (
                        <strong>
                          {setNumberNotation(investorChart.percentageDeference)}
                          %
                        </strong>
                        ){' '}
                        {investorChart.percentageDeference < 0
                          ? 'decrease'
                          : 'increase'}{' '}
                        in last 30 days
                      </>
                    }
                    chart={investorChart}
                  />
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
          <MDBox>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <RecentProperties />
              </Grid>
              <Grid item xs={12} md={6} lg={4}></Grid>
            </Grid>
          </MDBox>
        </MDBox>
      )}
    </>
  );
}

export default Dashboard;
